import { Datepicker } from 'application/ui/datepicker'
import type { IDatepickerConfig } from 'application/ui/datepicker'
import {
  formatDateParam,
  formatDateRangeLabel
} from 'application/utils/date-format'

/**
 * Максимальное количество видимых слотов расписания по умолчанию
 */
const MAX_VISIBLE_SLOTS = 7

/**
 * Класс для управления расписанием квеста
 *
 * Обрабатывает отображение слотов времени, кнопку "Показать еще",
 * и интеграцию с datepicker для фильтрации по датам
 */
export class Timetable {
  /** Кнопка "Показать еще" для расширения списка дней */
  private loadMoreButton: HTMLElement

  /** Основной элемент контейнера расписания */
  private element: HTMLElement

  /** Календарь фильтра по датам */
  private datepicker: Datepicker | null = null

  /** Кнопка «назад» уже под наблюдением */
  private historyWatched = false

  /**
   * Создает новый экземпляр Timetable
   *
   * @param {HTMLElement} element - HTML-элемент контейнера расписания с атрибутом [data-timetable]
   */
  constructor(element: HTMLElement) {
    this.element = element
    this.init()
  }

  /**
   * Инициализирует компонент расписания
   *
   * Настраивает кнопку "Показать еще", скрывает лишние дни
   * и инициализирует datepicker для выбора диапазона дат
   *
   * @private
   */
  private init() {
    this.loadMoreButton =
      this.element.querySelector<HTMLElement>('[data-load-more]')!

    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.showMoreSlots)
      this.hideExtraDays()
    }

    const datepickerContainer =
      this.element.querySelector<HTMLElement>('[data-datepicker]')!
    this.datepicker = new Datepicker(datepickerContainer, {
      dateFormat: 'Y-m-d',
      defaultDate: this.selectedDatesFromUrl(
        datepickerContainer.dataset.type === 'range'
      ),
      onChange: this.onDateChange
    } as IDatepickerConfig)

    this.initTooltipPositioning()
  }

  /**
   * Инициализирует позиционирование тултипов слотов при появлении.
   */
  private initTooltipPositioning(): void {
    this.element.addEventListener('pointerover', this.updateTooltipPosition)
    this.element.addEventListener('focusin', this.updateTooltipPosition)
    this.element.addEventListener('pointerout', this.resetTooltipPosition)
    this.element.addEventListener('focusout', this.resetTooltipPosition)
  }

  /**
   * Сдвигает тултип внутрь viewport после того, как CSS сделал его видимым.
   *
   * @param {Event} event - Событие появления тултипа.
   */
  private updateTooltipPosition = (event: Event): void => {
    const trigger = this.getTooltipTrigger(event)
    const tooltip = this.getTooltip(trigger)

    if (!tooltip) return

    window.requestAnimationFrame(() => {
      const tooltipRect = tooltip.getBoundingClientRect()
      const viewportPadding = 8
      let shiftX = 0

      if (tooltipRect.left < viewportPadding) {
        shiftX = viewportPadding - tooltipRect.left
      } else if (tooltipRect.right > window.innerWidth - viewportPadding) {
        shiftX = window.innerWidth - viewportPadding - tooltipRect.right
      }

      tooltip.style.setProperty('--tooltip-shift-x', `${shiftX}px`)
    })
  }

  /**
   * Сбрасывает сдвиг тултипа после скрытия.
   *
   * @param {Event} event - Событие скрытия тултипа.
   */
  private resetTooltipPosition = (event: Event): void => {
    const trigger = this.getTooltipTrigger(event)
    if (this.isMovingInsideTrigger(event, trigger)) return

    const tooltip = this.getTooltip(trigger)

    tooltip?.style.removeProperty('--tooltip-shift-x')
  }

  private getTooltipTrigger(event: Event): HTMLElement | null {
    const target = event.target as HTMLElement | null

    return target?.closest('.timetable-cell__icon') || null
  }

  private getTooltip(trigger: HTMLElement | null): HTMLElement | null {
    return (
      trigger?.parentElement?.querySelector<HTMLElement>(
        '.timetable-cell__tooltip'
      ) || null
    )
  }

  private isMovingInsideTrigger(
    event: Event,
    trigger: HTMLElement | null
  ): boolean {
    if (!trigger || !('relatedTarget' in event)) return false

    return trigger.contains(event.relatedTarget as Node | null)
  }

  /**
   * Скрывает дни расписания, превышающие максимальное количество видимых слотов
   *
   * Если количество дней меньше или равно MAX_VISIBLE_SLOTS,
   * кнопка "Показать еще" скрывается
   *
   * @private
   */
  private hideExtraDays() {
    const slots = this.element.querySelectorAll<HTMLElement>('[data-day]')

    if (slots.length <= MAX_VISIBLE_SLOTS) {
      this.loadMoreButton.classList.add('is-hidden')
      return
    }

    slots.forEach((slot, index) => {
      if (index >= MAX_VISIBLE_SLOTS) {
        slot.classList.add('is-hidden')
      }
    })
  }

  /**
   * Обработчик клика на кнопку "Показать еще"
   *
   * Отображает все скрытые дни и скрывает саму кнопку
   *
   * @private
   * @param {Event} event - Событие клика
   */
  private showMoreSlots = (event: Event) => {
    event.preventDefault()
    const slots = this.element.querySelectorAll<HTMLElement>(
      '[data-day].is-hidden'
    )
    slots.forEach((slot) => {
      slot.classList.remove('is-hidden')
    })

    this.loadMoreButton.classList.add('is-hidden')
    this.loadMoreButton.removeEventListener('click', this.showMoreSlots)
  }

  /**
   * Читает выбранный период из адреса страницы.
   *
   * Расписание фильтруется параметрами start_date/end_date, и после
   * перезагрузки календарь должен показывать тот же период, а не пустую
   * кнопку «Выбрать период».
   *
   * @private
   * @param {boolean} isRange - Календарь работает в режиме периода.
   * @return {string[]} Выбранные даты в формате «2026-08-25», если они есть в адресе.
   */
  private selectedDatesFromUrl(isRange: boolean): string[] {
    const params = new URL(window.location.href).searchParams

    // На странице расписания города день приходит параметром date,
    // на странице квеста — периодом start_date/end_date.
    const single = params.get('date')

    const dates = single
      ? [single]
      : ['start_date', 'end_date']
          .map((name) => params.get(name))
          .filter((value): value is string => Boolean(value))

    // В режиме периода одна дата обязана прийти завершённым диапазоном.
    // Иначе календарь считает выбор начатым, и первый же клик достраивает
    // период к прежней дате — вместо выбора нового дня.
    if (isRange && dates.length === 1) {
      return [dates[0], dates[0]]
    }

    return dates
  }

  /**
   * Обработчик изменения выбранных дат в datepicker
   *
   * При выборе даты(дат) обновляет URL с параметрами start_date и end_date,
   * добавляет якорь #timetableSection и перезагружает страницу
   *
   * @private
   * @param {Date[]} selectedDates - Массив выбранных дат (одна дата для single mode, две для range mode)
   */
  private onDateChange = (selectedDates: Date[]) => {
    const dateFrom = selectedDates[0]
    if (!dateFrom) {
      return
    }

    // Диапазон из одного дня — это одиночная дата: [d, d] приходит из
    // календаря в режиме периода, где выбор одной даты завершается ей же.
    let dateTo: Date | null = selectedDates[1] || null
    if (dateTo && formatDateParam(dateTo) === formatDateParam(dateFrom)) {
      dateTo = null
    }

    const url = new URL(window.location.href)

    // Расписание города читает date раньше start_date, поэтому прежний
    // выбор чипа «Завтра» перекрыл бы дату, выбранную в календаре.
    url.searchParams.delete('date')
    url.searchParams.set('start_date', formatDateParam(dateFrom))

    if (dateTo) {
      url.searchParams.set('end_date', formatDateParam(dateTo))
    } else {
      // Иначе после диапазона выбор одной даты оставил бы прежний конец
      // периода, и расписание не совпало бы с календарём.
      url.searchParams.delete('end_date')
    }

    this.datepicker?.setButtonLabel(
      formatDateRangeLabel([dateFrom, dateTo].filter(Boolean) as Date[], '')
    )

    // Страница квеста обновляет расписание на месте: перезагрузка с якорем
    // сначала показывала верх страницы и только потом прыгала к расписанию.
    // У расписания города разметка другая — там остаётся перезагрузка.
    if (this.element.hasAttribute('data-quests-schedule')) {
      url.hash = 'timetableSection'
      window.location.href = url.toString()
      return
    }

    void this.refreshSchedule(url)
  }

  /**
   * Загружает расписание для нового периода и подменяет его на странице.
   *
   * Заменяются только внутренности блока: легенда цен, чипы-пресеты, сами
   * слоты и кнопка «Показать еще». Корневой [data-timetable] не трогаем —
   * на нём висит делегированный обработчик открытия бронирования, а внутри
   * живёт календарь с открытым состоянием.
   *
   * @private
   * @param {URL} url - Адрес страницы с новым периодом.
   */
  private async refreshSchedule(url: URL): Promise<void> {
    this.element.classList.add('is-loading')

    try {
      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html' }
      })
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const html = await response.text()
      const fresh = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-timetable]')
      if (!fresh) {
        throw new Error('timetable block is missing in response')
      }

      this.replaceSection('.timetable__legend', fresh)
      this.replaceFilterChips(fresh)
      this.replaceSection('.timetable__content', fresh)
      this.replaceLoadMore(fresh)

      this.watchHistoryBack()
      window.history.pushState({}, '', url.toString())
    } catch {
      // Не вышло обновить на месте — открываем страницу целиком, как раньше.
      url.hash = 'timetableSection'
      window.location.href = url.toString()
    } finally {
      this.element.classList.remove('is-loading')
    }
  }

  /**
   * После подмены расписания кнопка «назад» меняет только адрес страницы.
   * Перезагружаем её, чтобы расписание снова совпало с адресом.
   *
   * @private
   */
  private watchHistoryBack(): void {
    if (this.historyWatched) {
      return
    }

    this.historyWatched = true
    window.addEventListener('popstate', () => window.location.reload())
  }

  /**
   * Меняет блок на его версию из свежего ответа: обе стороны могут
   * отсутствовать — например, легенды цен нет у квестов с одной ценой.
   *
   * @private
   * @param {string} selector - Селектор блока внутри расписания.
   * @param {Element} fresh - Свежий блок [data-timetable] из ответа.
   */
  private replaceSection(selector: string, fresh: Element): void {
    const current = this.element.querySelector(selector)
    const incoming = fresh.querySelector(selector)

    if (current && incoming) {
      current.replaceWith(incoming)
    } else if (current) {
      current.remove()
    } else if (incoming) {
      if (selector === '.timetable__legend') {
        this.element.prepend(incoming)
      } else {
        this.element.querySelector('.timetable__filters')?.after(incoming)
      }
    }
  }

  /**
   * Обновляет чипы-пресеты периода: их active-состояние считает сервер.
   *
   * @private
   * @param {Element} fresh - Свежий блок [data-timetable] из ответа.
   */
  private replaceFilterChips(fresh: Element): void {
    const filters = this.element.querySelector('.timetable__filters')
    if (!filters) {
      return
    }

    filters
      .querySelectorAll('.timetable__filters-item')
      .forEach((chip) => chip.remove())
    fresh
      .querySelectorAll('.timetable__filters-item')
      .forEach((chip) => filters.append(chip))
  }

  /**
   * Меняет кнопку «Показать еще» и заново скрывает лишние дни:
   * обработчик висел на прежней кнопке и ушёл вместе с ней.
   *
   * @private
   * @param {Element} fresh - Свежий блок [data-timetable] из ответа.
   */
  private replaceLoadMore(fresh: Element): void {
    const current = this.element.querySelector<HTMLElement>('[data-load-more]')
    const incoming = fresh.querySelector<HTMLElement>('[data-load-more]')

    if (current && incoming) {
      current.replaceWith(incoming)
    }

    this.loadMoreButton =
      this.element.querySelector<HTMLElement>('[data-load-more]')!
    if (this.loadMoreButton) {
      this.loadMoreButton.addEventListener('click', this.showMoreSlots)
      this.hideExtraDays()
    }
  }
}
